import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Camera,
  Circle,
  Crosshair,
  Loader2,
  Pause,
  Square,
  WifiOff,
} from 'lucide-react';
import clsx from 'clsx';
import { LiveStreamPlayer } from './LiveStreamPlayer';
import { LiveStreamTimer } from './LiveStreamTimer';
import { LatencyBadge } from './LatencyBadge';
import { TeamLiveStrap } from './TeamLiveStrap';
import { useNearViewport } from '@/hooks/useVisibleRange';
import type { TeamLiveTileDrag } from './TeamLiveSortableGrid';
import type { LiveStreamDisplayStatus, TeamLiveStreamEntry } from '@/types';
import { formatUserName } from '@/utils/userName';

// Statuses for which mounting the WHEP player makes sense; everything else
// gets a placeholder that keeps the tile (and its slot) visible.
const PLAYER_STATUSES = new Set<LiveStreamDisplayStatus>([
  'live',
  'starting',
  'stopping',
]);

/**
 * Status as a strap accent colour. This replaces the light-theme status pill
 * that used to sit in the tile's top-right corner (issue #738) — on a black
 * multiview a coloured bar reads faster than a pastel badge, and it costs no
 * horizontal space.
 */
const ACCENT_CLASSES: Record<LiveStreamDisplayStatus, string> = {
  live: 'bg-green-500',
  starting: 'bg-blue-400',
  stopping: 'bg-orange-400',
  planned: 'bg-yellow-400',
  offline: 'bg-secondary-500',
  failed: 'bg-red-500',
  ended: 'bg-secondary-500',
  deleted: 'bg-secondary-600',
  unknown: 'bg-secondary-500',
};

/** Local reticle mark: where this viewer tapped and when (trackcast #328). */
export interface TeamLiveFocusMark {
  x: number;
  y: number;
  sentAt: number;
  applied: boolean;
}

export interface TeamLiveTileProps {
  entry: TeamLiveStreamEntry;
  /** Called with the next desired recording state. */
  onToggleRecording: (desired: boolean) => void;
  isToggling: boolean;
  focusArmed?: boolean;
  onToggleFocusArm?: () => void;
  onFocusPoint?: (nx: number, ny: number) => void;
  isFocusing?: boolean;
  focusMark?: TeamLiveFocusMark | null;
  /** Drag affordance from the sortable grid; null when reordering is off. */
  drag?: TeamLiveTileDrag | null;
  /** Cam on/off authority for the viewer (wall remote cam control). */
  canControl?: boolean;
  /** Server-side pending cam-off declaration (fresh + un-acked). */
  pendingCamOff?: boolean;
  /** Local cam-off request in flight. */
  isRequestingCam?: boolean;
  /** Director cam-off action (ends the live stream on the phone). */
  onRequestOff?: () => void;
  /**
   * Mobile playback windowing: when true the player unmounts while the tile
   * is more than a viewport away (2026-09-25 mobile tile layout spec).
   */
  windowingEnabled?: boolean;
  /**
   * Video-tap fallback when focus aim is NOT armed (mobile: opens the focus
   * viewer). While armed, video taps keep reporting focus points.
   */
  onExpand?: () => void;
  /** Force-hide the strap (focus viewer landscape auto-hide). Instant, so reduced motion needs no handling. */
  strapHidden?: boolean;
  /** Forced player unmount (e.g. the same stream is open in the focus viewer). */
  paused?: boolean;
}

/**
 * One member's stream on the Team Live multiview: WHEP player (or status
 * placeholder) under a single bottom strap that carries identity, elapsed
 * time, latency and the director recording control with its three states —
 * idle, requested (device hasn't acked), recording. The strap is also the drag
 * surface for reordering; the player appends mute/fullscreen to it.
 */
export function TeamLiveTile({
  entry,
  onToggleRecording,
  isToggling,
  focusArmed,
  onToggleFocusArm,
  onFocusPoint,
  isFocusing,
  focusMark,
  drag,
  canControl,
  pendingCamOff,
  isRequestingCam,
  onRequestOff,
  windowingEnabled,
  onExpand,
  strapHidden,
  paused,
}: TeamLiveTileProps) {
  const { t } = useTranslation('pages');
  const { liveStream, member, session, whepUrl, canRecord } = entry;
  const status = liveStream.displayStatus ?? 'unknown';
  const { ref: visibilityRef, isNear } = useNearViewport(!!windowingEnabled);
  const dormant = (!!windowingEnabled && !isNear) || !!paused;
  const showPlayer = PLAYER_STATUSES.has(status) && !!whepUrl && !dormant;
  const acked = !!liveStream.deviceStatus?.recording;
  const desired = !!liveStream.recordingDesired;
  const recordingControlAvailable =
    liveStream.recordingControlAvailable !== false;
  const memberName = formatUserName(member);
  const statusLabel = t(`sessions.detail.liveStream.status.${status}`);

  // Reticle lifetime: re-render every second while a mark is visible so the
  // focusing -> no-response -> gone transitions happen without a poll. An
  // applied mark is static — no ticker can change anything for it.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!focusMark || focusMark.applied) return;
    const timer = setInterval(() => setTick((tick) => tick + 1), 1000);
    return () => clearInterval(timer);
  }, [focusMark]);

  const markAge = focusMark ? Date.now() - focusMark.sentAt : 0;
  const markState: 'focusing' | 'focused' | 'noResponse' | null = !focusMark
    ? null
    : focusMark.applied
      ? 'focused'
      : markAge < 8000
        ? 'focusing'
        : markAge < 10_000
          ? 'noResponse'
          : null;

  const recordControl =
    canRecord && recordingControlAvailable ? (
      <button
        type="button"
        disabled={isToggling}
        onClick={() => onToggleRecording(!(acked || desired))}
        // Must not start a strap drag.
        onPointerDown={(event) => event.stopPropagation()}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors disabled:opacity-50',
          acked
            ? 'bg-red-600 text-white hover:bg-red-700'
            : desired
              ? 'border border-amber-400/60 bg-black/50 text-amber-300 backdrop-blur-sm hover:bg-black/70'
              : 'border border-red-500/60 bg-black/50 text-red-300 backdrop-blur-sm hover:bg-black/70'
        )}
      >
        {acked ? (
          <Square className="h-2.5 w-2.5 fill-current" />
        ) : desired ? (
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
        ) : (
          <Circle className="h-2.5 w-2.5 fill-current" />
        )}
        {acked
          ? t('teamLive.stopRecording')
          : desired
            ? t('teamLive.recordPending')
            : t('teamLive.record')}
      </button>
    ) : null;

  const focusControl =
    canRecord &&
    recordingControlAvailable &&
    status === 'live' &&
    onToggleFocusArm ? (
      <button
        type="button"
        disabled={isFocusing}
        onClick={onToggleFocusArm}
        // Must not start a strap drag.
        onPointerDown={(event) => event.stopPropagation()}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors disabled:opacity-50',
          focusArmed
            ? 'bg-amber-400 text-black hover:bg-amber-300'
            : 'border border-amber-400/60 bg-black/50 text-amber-300 backdrop-blur-sm hover:bg-black/70',
        )}
      >
        <Crosshair className="h-2.5 w-2.5" />
        {t('teamLive.focus')}
      </button>
    ) : null;

  // Cam off (wall remote cam control): a two-click confirm — the first click
  // arms (amber), the second fires. Killing a live broadcast must never be a
  // single misclick. Auto-disarms after 4 s or when the request goes pending.
  const [, setCamTick] = useState(0);
  const [camOffArmed, setCamOffArmed] = useState(false);
  useEffect(() => {
    if (!camOffArmed) return;
    const timer = setTimeout(() => setCamOffArmed(false), 4_000);
    return () => clearTimeout(timer);
  }, [camOffArmed]);
  useEffect(() => {
    if (pendingCamOff || isRequestingCam) setCamOffArmed(false);
  }, [pendingCamOff, isRequestingCam]);
  const camStopping = pendingCamOff || isRequestingCam;
  const camOffControl =
    canControl && onRequestOff && PLAYER_STATUSES.has(status) ? (
      <button
        type="button"
        disabled={camStopping}
        onClick={() => {
          if (camOffArmed) {
            setCamOffArmed(false);
            onRequestOff();
          } else {
            setCamOffArmed(true);
            setCamTick((tick) => tick + 1);
          }
        }}
        // Must not start a strap drag.
        onPointerDown={(event) => event.stopPropagation()}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors disabled:opacity-50',
          camStopping
            ? 'border border-sky-400/60 bg-black/50 text-sky-300 backdrop-blur-sm'
            : camOffArmed
              ? 'bg-amber-400 text-black hover:bg-amber-300'
              : 'border border-white/30 bg-black/50 text-white/80 backdrop-blur-sm hover:bg-black/70',
        )}
      >
        {camStopping ? (
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
        ) : (
          <Camera className="h-2.5 w-2.5" />
        )}
        {camStopping
          ? t('teamLive.camStopping')
          : camOffArmed
            ? t('teamLive.camOffConfirm')
            : t('teamLive.camOff')}
      </button>
    ) : null;

  const strap = (
    <TeamLiveStrap
      title={memberName}
      subtitle={
        session
          ? (session.vehicleName ?? session.title)
          : t('liveStreams.unassigned')
      }
      statusLabel={statusLabel}
      accentClassName={ACCENT_CLASSES[status] ?? ACCENT_CLASSES.unknown}
      drag={drag}
      metrics={
        status === 'live' ? (
          <>
            <LiveStreamTimer
              startTime={liveStream.startedAt}
              isRunning
              className="whitespace-nowrap text-white/75"
            />
            {session ? (
              <LatencyBadge sessionId={session.id} variant="strap" />
            ) : null}
          </>
        ) : undefined
      }
    >
      <>
        {recordControl}
        {focusControl}
        {camOffControl}
      </>
    </TeamLiveStrap>
  );

  return (
    <div
      ref={visibilityRef}
      className={clsx(
        'relative min-h-0 overflow-hidden rounded-lg border bg-secondary-900',
        acked ? 'border-red-600 ring-2 ring-red-600' : 'border-white/10'
      )}
    >
      <div
        className={clsx(
          'h-full w-full',
          onExpand && !showPlayer && 'cursor-pointer'
        )}
        data-testid="tile-body"
        role={onExpand && !showPlayer ? 'button' : undefined}
        tabIndex={onExpand && !showPlayer ? 0 : undefined}
        onClick={onExpand && !showPlayer ? onExpand : undefined}
        onKeyDown={
          onExpand && !showPlayer
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  // Space must activate, not scroll the page.
                  event.preventDefault();
                  onExpand();
                }
              }
            : undefined
        }
      >
        {showPlayer && whepUrl ? (
          <LiveStreamPlayer
            src={whepUrl}
            title={session?.title ?? t('liveStreams.unassigned')}
            startTime={liveStream.startedAt}
            isRunning={status === 'live'}
            variant="fill"
            strap={strap}
            strapProps={{
              ...drag?.strapProps,
              style: {
                ...drag?.strapProps?.style,
                ...(strapHidden
                  ? { opacity: 0, pointerEvents: 'none' as const }
                  : {}),
              },
              'data-testid': 'tile-strap',
            }}
            onVideoTap={
              focusArmed
                ? onFocusPoint
                : onExpand
                  ? () => onExpand()
                  : undefined
            }
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-white/60">
            {dormant ? (
              <Pause className="h-6 w-6" />
            ) : status === 'starting' ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <WifiOff className="h-6 w-6" />
            )}
            <span className="text-xs">
              {dormant
                ? t('teamLive.dormant')
                : t(`teamLive.tileStatus.${status}`, {
                    defaultValue: t('teamLive.tileStatus.unknown'),
                  })}
            </span>
          </div>
        )}
      </div>

      {/*
        Without a player there is no bottom bar to host the strap, so the tile
        renders it directly. Same component, same position — the placeholder
        must not look like a different kind of tile.
      */}
      {!showPlayer ? (
        <div
          {...drag?.strapProps}
          data-testid="tile-strap"
          className={clsx(
            'absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/75 via-black/45 to-transparent px-2 py-1.5',
            strapHidden && 'pointer-events-none opacity-0'
          )}
        >
          {strap}
        </div>
      ) : null}

      {/* Recording is the one state that must be visible without hovering. */}
      {acked ? (
        <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1.5 rounded bg-red-600 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white">
          <span className="h-1.5 w-1.5 animate-pulse rounded-sm bg-white" />
          {t('teamLive.recordingTag')}
        </span>
      ) : null}

      {focusArmed ? (
        <div className="pointer-events-none absolute inset-x-0 top-2 flex justify-center">
          <span className="rounded-md border border-amber-400/40 bg-black/75 px-3 py-1 text-xs font-semibold text-amber-300 backdrop-blur-sm">
            {t('teamLive.focusArmedBanner')}
          </span>
        </div>
      ) : null}

      {markState && focusMark ? (
        <div
          data-testid="focus-mark"
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${focusMark.x * 100}%`,
            top: `${focusMark.y * 100}%`,
          }}
        >
          <div
            className={clsx(
              'relative h-9 w-9 rounded-full border-2',
              markState === 'focused'
                ? 'border-white text-white shadow-[0_0_10px_rgba(255,255,255,0.4)]'
                : markState === 'focusing'
                  ? 'border-amber-400 text-amber-400'
                  : 'border-white/35 text-white/35'
            )}
          >
            {/* crosshair lines inherit the ring colour via text- */}
            <span className="absolute left-1/2 top-[-6px] bottom-[-6px] w-px bg-current" />
            <span className="absolute top-1/2 left-[-6px] right-[-6px] h-px bg-current" />
          </div>
          <p
            className={clsx(
              'mt-1 text-center text-[10px] font-semibold',
              markState === 'focused'
                ? 'text-white'
                : markState === 'focusing'
                  ? 'text-amber-300'
                  : 'text-white/50'
            )}
          >
            {markState === 'focused'
              ? t('teamLive.focused')
              : markState === 'focusing'
                ? t('teamLive.focusing')
                : t('teamLive.noFocusResponse')}
          </p>
        </div>
      ) : null}
    </div>
  );
}
